import {
	CardDocumentId,
	type ChatDocumentId,
	PreprintDocumentId,
	WhiteboardDocumentId,
} from "../classes";
import { isDocumentId } from "../validation";
import { fromDocumentIdString, parseChatDocumentId } from "./from-string";

type CardEntityDocumentId = CardDocumentId | PreprintDocumentId;
export type NavbarDocumentKind = "folder" | "board" | "card" | "chat";

export type NavbarDocumentParseSuccess =
	| { ok: true; kind: "folder" }
	| {
			ok: true;
			kind: "board";
			documentId: WhiteboardDocumentId;
	  }
	| {
			ok: true;
			kind: "card";
			documentId: CardEntityDocumentId;
	  }
	| {
			ok: true;
			kind: "chat";
			documentId: ChatDocumentId;
	  };

export type NavbarDocumentParseFailure = {
	ok: false;
	error: string;
};

export type NavbarDocumentParseResult =
	| NavbarDocumentParseSuccess
	| NavbarDocumentParseFailure;

/**
 * Safely maps a navbar branch key + kind to a typed document id.
 * Never throws — bad keys return `{ ok: false }` so the nav tree can skip
 * or degrade without unmounting the section.
 */
export function tryParseNavbarDocumentId(
	key: string,
	kind: NavbarDocumentKind,
): NavbarDocumentParseResult {
	try {
		if (kind === "folder") {
			return { ok: true, kind: "folder" };
		}

		if (kind === "board") {
			return {
				ok: true,
				kind: "board",
				documentId: parseWhiteboardKey(key),
			};
		}

		if (kind === "card") {
			return {
				ok: true,
				kind: "card",
				documentId: parseCardEntityKey(key),
			};
		}

		if (kind === "chat") {
			return {
				ok: true,
				kind: "chat",
				documentId: parseChatDocumentId(key),
			};
		}

		return {
			ok: false,
			error: `Unsupported navbar document kind: ${kind}`,
		};
	} catch (error) {
		return {
			ok: false,
			error:
				error instanceof Error
					? error.message
					: "Failed to parse navbar document id",
		};
	}
}

function parseWhiteboardKey(key: string): WhiteboardDocumentId {
	if (isDocumentId(key)) {
		return fromDocumentIdString(key, WhiteboardDocumentId);
	}
	return new WhiteboardDocumentId(key);
}

function parseCardEntityKey(key: string): CardEntityDocumentId {
	if (isDocumentId(key)) {
		const documentId = fromDocumentIdString(key);
		if (
			documentId instanceof CardDocumentId ||
			documentId instanceof PreprintDocumentId
		) {
			return documentId;
		}
		throw new Error(`Document type "${documentId.type}" is not a card entity`);
	}
	return new CardDocumentId(key);
}
